import * as z from "zod";

export const CUSTOM_HTML_MAX_LENGTH = 102_400;
export const CUSTOM_HTML_CODE_PART_MAX_LENGTH = 50_000;
export const CUSTOM_HTML_CODE_PARTS_MAX = 5;
export const CUSTOM_HTML_URL_PART_MAX_LENGTH = 2000;
export const CUSTOM_HTML_URL_PARTS_MAX = 10;

export function isAllowedBlockScriptUrl(value: string): boolean {
  if (value.length > CUSTOM_HTML_URL_PART_MAX_LENGTH) {
    return false;
  }
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === "https:" &&
      parsed.username === "" &&
      parsed.password === ""
    );
  } catch {
    return false;
  }
}

/** One ordered piece of a block's install code: inline JS or an external script. */
export const blockCodePartSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("code"),
    id: z.string().min(1),
    code: z.string().max(CUSTOM_HTML_CODE_PART_MAX_LENGTH),
  }),
  z.object({
    kind: z.literal("url"),
    id: z.string().min(1),
    url: z
      .string()
      .max(CUSTOM_HTML_URL_PART_MAX_LENGTH)
      .refine(isAllowedBlockScriptUrl),
  }),
]);
export type BlockCodePart = z.infer<typeof blockCodePartSchema>;

// Each kind is capped independently; one kind can't inflate the other's room.
const blockCodeSchema = z
  .array(blockCodePartSchema)
  .refine(
    (parts) =>
      parts.filter((part) => part.kind === "code").length <=
      CUSTOM_HTML_CODE_PARTS_MAX,
    {
      message: `At most ${CUSTOM_HTML_CODE_PARTS_MAX} inline code parts allowed`,
    },
  )
  .refine(
    (parts) =>
      parts.filter((part) => part.kind === "url").length <=
      CUSTOM_HTML_URL_PARTS_MAX,
    {
      message: `At most ${CUSTOM_HTML_URL_PARTS_MAX} script URL parts allowed`,
    },
  );

/** Merchant-authored HTML/JS a block hosts; `code` preserves paste order. */
export const blockContentSchema = z.object({
  html: z.string().max(CUSTOM_HTML_MAX_LENGTH).default(""),
  code: blockCodeSchema.optional(),
});
export interface BlockContent extends z.infer<typeof blockContentSchema> {}

/** True when code implies the block runs in the cross-origin block service. */
export function isFramedBlock(block: Pick<BlockContent, "code">): boolean {
  return (block.code ?? []).some(
    (part) => part.kind === "url" || part.code.trim().length > 0,
  );
}
